import clsx from "clsx";
import React, { HTMLAttributes, useEffect, useState } from "react";
import { Status } from "../../journey/UserJourneyReducer";
import { OverlayTrigger, Popover, Spinner } from "react-bootstrap";
import { t } from "../../i18n";
import { PaymentStatus, PaymentType, Product } from "../../http/protocol";
import { getAmountClass, getTypeIcon } from "../TransactionUtils";
import Icon from "../../core/ui/atoms/Icon";
import { ReactNode } from "react-markdown";

export type TransactionTypes = {
    product?: Product | string;
    type?: string;
    game?: string;
    timeStamp?: string | number;
    class?: string;
};

export type TransactionReference = {
    reference?: string
}

export type TransactionAmount = {
    amount?: number | string;
    currency?: string;
}

export type TransactionData = TransactionAmount | TransactionReference | TransactionTypes;



export interface TransactionComponentPropss<T> {
    value: TransactionData | T,
    identifier: string;
}



/**
 * @ColumnComponent - General Amount column ( POKER, CASINO, SPORTSBOOK )
 * 
 */
/* ------------------------------------------------------------------------------------------------------------------- */
/* ------------------------------------------------------------------------------------------------------------------- */

export const TransactionAmountComponent = <T extends TransactionData>({ value, identifier }: TransactionComponentPropss<T>): JSX.Element => {
    return (
        <div className={clsx("transaction-table__cell-component", `transaction-table__cell-component--${identifier}`)}>
            {
                identifier === "amount" && "amount" in value && (
                    <>
                        <span className={clsx("transaction-table__cell-content", "transaction-table__cell-content--amount", getAmountClass(value.amount as any))}>{value.amount}</span>
                        <span className={clsx("transaction-table__cell-content", "transaction-table__cell-content--currency", getAmountClass(value.amount as any))}>{value.currency}</span>
                    </>
                )
            }
        </div >
    );
};


export const MemoizedTransactionAmountComponent = React.memo(TransactionAmountComponent);



/**
 * @ColumnComponent - General Type column ( POKER, CASINO, SPORTSBOOK )
 * 
 */
/* ------------------------------------------------------------------------------------------------------------------- */
/* ------------------------------------------------------------------------------------------------------------------- */

export const TransactionTypeComponent = <T extends TransactionData>({ value, identifier }: TransactionComponentPropss<T>): JSX.Element => {
    return (
        <div className={clsx("transaction-table__cell-component", `transaction-table__cell-component--${identifier}`)}>
            {identifier === "type" && "product" in value && (
                <>
                    <div className={clsx('transaction-table__cell-content--wapper')}>
                        <span className={clsx("transaction-table__cell-content", "transaction-table__cell-content--icon")}>{<Icon className="transaction-icon" name={value.class !== 'positive' ? 'deposit' : 'withdraw'} />}</span>
                        <span className={clsx("transaction-table__cell-content", "transaction-table__cell-content--product")}>{value.product}</span>
                        <span className={clsx("transaction-table__cell-content", "transaction-table__cell-content--type")}>{value.type}</span>
                        <span className={clsx("transaction-table__cell-content", "transaction-table__cell-content--game")}>{value.game}</span>
                    </div>
                    <div className={clsx('transaction-table__cell-content--wapper')}>
                        <span className={clsx("transaction-table__cell-content", "transaction-table__cell-content--timeStamp")}>{value.timeStamp}</span>
                    </div>
                </>
            )}
        </div >
    );
};


export const MemoizedTransactionTypeComponent = React.memo(TransactionTypeComponent);



/**
 * @ColumnComponent - General Type column ( POKER, CASINO, SPORTSBOOK )
 * 
 */
/* ------------------------------------------------------------------------------------------------------------------- */
/* ------------------------------------------------------------------------------------------------------------------- */

export const TransactionRefernceComponent = <T extends TransactionData>({ value, identifier }: TransactionComponentPropss<T>): JSX.Element => {
    return (
        <div className={clsx("transaction-table__cell-component", `transaction-table__cell-component--${identifier}`)}>
            {
                identifier === "reference" && "reference" in value && (
                    <span className={clsx("transaction-table__cell-content", "transaction-table__cell-content--reference")}>{value.reference}</span>
                )
            }
        </div >
    );
};


export const MemoizedTransactionRefernceComponent = React.memo(TransactionRefernceComponent);



/**
 * @ColumnComponent - Table pagination and status component
 * 
 */
/* ------------------------------------------------------------------------------------------------------------------- */
/* ------------------------------------------------------------------------------------------------------------------- */


interface TableStatusAndPaginationProps {
    status: Status;
    handlePagination: () => void;
    hasTransactions: boolean;
    nrOfTransactions?: number;
    activeCurrency?: { name?: string, code?: string };
}

export const TableStatusAndPaginations: React.FC<TableStatusAndPaginationProps> = ({ status, handlePagination, hasTransactions, activeCurrency, nrOfTransactions }) => {
    // if transactions list ( limit = 20 ) is less then 20, disable pagination ( no more transactions in db )
    const disableLoading = nrOfTransactions ? nrOfTransactions < 20 : false
    return (
        <div className={clsx("transaction-table__status-pagination")}>
            {
                !hasTransactions && status == Status.LOADING && (
                    <div className={clsx("transaction-table", "transaction-table__spinner")}>
                        <Spinner animation="border" className={clsx("transaction-table__spinner--spinner")} />
                    </div>
                )
            }
            {
                !disableLoading && hasTransactions && (
                    <div className={clsx("transaction-table", "transaction-table__paginate")}>
                        <button className="transaction-table-paginate--btn btn btn-cta" onClick={handlePagination}>
                            {
                                status == Status.LOADING ? <Spinner size='sm' role='status' animation="border" className={clsx("transaction-table__spinner--spinner")} /> : t("cashier.more")
                            }
                        </button>
                    </div>
                )
            }
            {
                status == Status.ERROR && (
                    <div className={clsx("transaction-table", "transaction-table__error")}>
                        <span className={clsx("transaction-table__error--span")}>{t('transactions.transaction_wallet_table.error')}</span>
                    </div>
                )
            }
            {
                !hasTransactions && status == Status.DONE && (
                    <div className="transaction-table__status">
                        <span className={clsx("transaction-table__no-data--span")}>{t("transactions.messages.no-transactions-title")}</span>
                        <div className={"currency-icon-container"}>
                            <i className={"currency-icon currency-icon-" + activeCurrency?.code?.toLocaleLowerCase()}></i>
                        </div>
                        <span className={clsx("transaction-table__no-data--span")}>{activeCurrency?.name}</span>
                    </div>
                )
            }

        </div>
    )
}




type TransactionComponentProps<T> = {
    value: T;
};

/**
 * @ColumnComponent - Amount Column - Wallet Transaction
 * 
 */
/* ------------------------------------------------------------------------------------------------------------------- */
/* ------------------------------------------------------------------------------------------------------------------- */

const WalletTransactionCellAmountComponent = <T extends { amount: string, currency: string, status?: PaymentStatus }>({ value }: TransactionComponentProps<T>): JSX.Element => {
    const { amount, currency, status } = value;
    const numericAmount = Number(amount); // Convert to number
    const amountClass = getAmountClass(numericAmount, undefined, status);

    return (
        <div className={clsx("transaction-table__cell-component", "transaction-table__cell-component--amount")}>
            <span className={clsx("transaction-table__cell-content", "transaction-table__cell-content--value", amountClass)}>
                {status === PaymentStatus.CANCELED || status === PaymentStatus.DECLINED ? Math.abs(numericAmount).toString() : amount}
            </span>
            <span className={clsx("transaction-table__cell-content", "transaction-table__cell-content--currency", amountClass)}>{currency}</span>
        </div>
    );
};

export const MemoizedTransactionCellAmountComponent = React.memo(WalletTransactionCellAmountComponent);


/**
 * @ColumnComponent - Status Column - Wallet Transaction
 * 
 */
/* ------------------------------------------------------------------------------------------------------------------- */
/* ------------------------------------------------------------------------------------------------------------------- */

interface TransactionCellStatusComponentProps<T> extends HTMLAttributes<HTMLDivElement> {
    value: T;
}

const WalletTransactionCellStatusComponent = <T extends { name: string, classname: string }>({ value, ...props }: TransactionCellStatusComponentProps<T>): JSX.Element => {
    const { name, classname } = value;

    return (
        <div className={clsx("transaction-table__cell-component", "transaction-table__cell-component--status")}>
            <div className={clsx("transaction-table__cell-content", "transaction-table__cell-content--status-label", classname)} {...props}>
                <span className={clsx("transaction-table__cell-content--value")}>{name}</span>
            </div>
        </div>
    );
};

export const MemoizedTransactionCellTransactioComponent = React.memo(WalletTransactionCellStatusComponent);

/**
 * @ColumnComponent - Reference Column - Wallet Transaction 
 * 
 */
/* ------------------------------------------------------------------------------------------------------------------- */
/* ------------------------------------------------------------------------------------------------------------------- */

const WalletTransactionCellReferenceComponent = <T extends string>({ value }: TransactionComponentProps<T>): JSX.Element => {
    return (
        <div className={clsx("transaction-table__cell-component", "transaction-table__cell-component--reference")}>
            <span className={clsx("transaction-table__cell-content", "transaction-table__cell-content--value")}>{value}</span>
        </div>
    );
};

export const MemoizedTransactionCellReferenceComponent = React.memo(WalletTransactionCellReferenceComponent);



/**
 * @ColumnComponent - Type Column - Wallet Transaction 
 * 
 */
/* ------------------------------------------------------------------------------------------------------------------- */
/* ------------------------------------------------------------------------------------------------------------------- */

const WalletTransactionCellTypeComponent = <T extends { type: PaymentType, translations: string, class: string }>({ value }: TransactionComponentProps<T>): JSX.Element => {
    const typeIcon = getTypeIcon(value.type);

    return (
        <div className={clsx("transaction-table__cell-component", "transaction-table__cell-component--type")}>
            <span className={clsx("transaction-table__cell-content", "transaction-table__cell-content--icon")}><Icon className="transaction-icon" name={typeIcon} /></span>
            <span className={clsx("transaction-table__cell-content", "transaction-table__cell-content--value")}>{value.translations}</span>
        </div>
    );
};

export const MemoizedTransactionCellTypeComponent = React.memo(WalletTransactionCellTypeComponent);

/**
 * @ColumnComponent - Timestamp Column - Wallet Transaction 
 */
/* ------------------------------------------------------------------------------------------------------------------- */
/* ------------------------------------------------------------------------------------------------------------------- */
const WalletTransactionCellTimeStampComponent = <T extends string>({ value }: TransactionComponentProps<T>): JSX.Element => {
    return (
        <div className={clsx("transaction-table__cell-component", "transaction-table__cell-component--timeStamp")}>
            <span className={clsx("transaction-table__cell-content", "transaction-table__cell-content--value")}>{value}</span>
        </div>
    );
};

export const MemoizedTransactionCellTimeStampComponent = React.memo(WalletTransactionCellTimeStampComponent);


/**
 * @ColumnComponent - Cancel Payment Column - Wallet Transaction 
 * 
 */
/* ------------------------------------------------------------------------------------------------------------------- */
/* ------------------------------------------------------------------------------------------------------------------- */

interface TransactionCancelPaymentProps<T> extends HTMLAttributes<HTMLButtonElement> {
    value: T;
}
const WalletTransactionColumnCancelComponent = <T extends { action: any, hasAction: boolean }>({ value, ...props }: TransactionCancelPaymentProps<T>): JSX.Element => {
    return (
        <div className={clsx("transaction-table__cell-component", "transaction-table__cell-component--cancel")}>
            <div className={clsx()}>
                {value.hasAction && (
                    <button className={clsx("transaction-table__cell-content", "transaction-table__cell-content--button")} {...props}>
                        Cancel
                    </button>
                )}
            </div>
        </div>
    );
}

export const MemoizedTransactionColumnCancelComponent = React.memo(WalletTransactionColumnCancelComponent);




/**
 * @ColumnComponent - Transaction Id Column - Wallet Transaction 
 * 
 */
/* ------------------------------------------------------------------------------------------------------------------- */
/* ------------------------------------------------------------------------------------------------------------------- */

interface TransactionTransactionIdProps<T> extends HTMLAttributes<HTMLDivElement> {
    value: T;
}

const WalletTransactionColumnTransactionIdComponent = <T extends { transactionId: any, referenceUrl: string, status: PaymentStatus }>({ value, ...props }: TransactionTransactionIdProps<T>): JSX.Element => {
    const { transactionId, referenceUrl, status } = value;
    const pendingValue = status === PaymentStatus.PENDING || status === PaymentStatus.INITIATED;
    const inputValue = pendingValue ? t('transactions.status.pending-approval') : transactionId;

    const copyToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(transactionId);

        } catch (err) {
            console.error("Failed to copy: ", err);
        }
    };

    return (
        <div className={clsx("transaction-table__cell-component", "transaction-table__cell-component--transactionId")} {...props}>
            {(transactionId) && (
                <CopyPopOver info={"Copied"} >
                    <div onClick={copyToClipboard}>
                        <input
                            className={clsx("transaction-table__cell-content", "transaction-table__cell-content--input")}
                            value={inputValue}
                            readOnly
                        />
                        {!pendingValue && referenceUrl && (
                            <a className={clsx("transaction-table__cell-content", "transaction-table__cell-content--link")} href={referenceUrl} target="_blank" rel="noopener noreferrer">
                                {<Icon name="link" size={16} />}
                            </a>
                        )}
                    </div>
                </CopyPopOver>
            )}
        </div>
    )
};

export const MemoizedTransactionColumnTransactionIdComponent = React.memo(WalletTransactionColumnTransactionIdComponent);


/**
 * @TableComponent - Table header info - Wallet Transaction 
 * 
 */
/* ------------------------------------------------------------------------------------------------------------------- */
/* ------------------------------------------------------------------------------------------------------------------- */


interface InfoIconProps extends React.HTMLAttributes<HTMLDivElement> {
    variant: string
}

export const InfoIcon: React.FC<InfoIconProps> = ({ className, variant = "/info", ...props }) => {
    return (
        <div className={clsx("transaction-info-icon", className)} {...props}>
            <Icon name={variant as any} size={14} />
        </div>
    )
};



/**
 * @TableComponent - PopOver copie to clipboard
 * 
 */
/* ------------------------------------------------------------------------------------------------------------------- */
/* ------------------------------------------------------------------------------------------------------------------- */

interface PopOverProps {
    info: string;
    children: ReactNode | ReactNode[];
}

export const CopyPopOver: React.FC<PopOverProps> = ({ info, children }) => {
    const [show, setShow] = useState(false);

    const togglePopover = () => setShow(!show);
    const hidePopover = () => setShow(false);

    useEffect(() => {
        if (show) {
            setTimeout(() => {
                hidePopover()
            }, 380)
        }
    }, [show])

    const popoverContent = (
        <Popover id="popover-trigger-click" title="Popover">
            <div className="transaction-popover p-2">
                <p className="m-0 p-0 fs-7 text-gray-600 copy-label">{info}</p>
            </div>
        </Popover>
    );

    return (
        <div className="overtop" onClick={togglePopover}>
            <OverlayTrigger
                show={show}
                placement="top"
                overlay={popoverContent}
                rootClose

            >
                {children as JSX.Element}
            </OverlayTrigger>
        </div>
    );
};